import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/design-system';
import type { PinnedPreset } from './api';
import { Gallery } from './Gallery';
import { useStructureLabels } from './labels';
import { Outline } from './Outline';
import { PageStack } from './PageStack';
import { PreviewPanel, type StructureCtx } from './parts';
import { MAX_PINS, togglePin, updateAt } from './useStructure';
import s from './structure.module.css';

/**
 * «По списку» (DESIGN_SPEC §4.6 List mode): the outline, the gallery of the selected section and the
 * page preview. Hovering a design tries it on in the preview («Примерка»); a click chooses it (0–N
 * per section, C4), with a toast.
 */
export function ListMode({ doc, selected, select, filters, setFilters, openConstructor }: StructureCtx) {
  const { t } = useTranslation('structure');
  const toast = useToast();
  const { section, designName } = useStructureLabels();
  const [hov, setHov] = useState<PinnedPreset | null>(null);
  const cur = doc.secs[selected];

  const choose = (i: number) => {
    select(i);
    setHov(null);
    setFilters({ ...filters, layout: 'all' });
  };
  const pick = (pin: PinnedPreset | null) => {
    if (!cur) return;
    const name = section(cur.section_type);
    setHov(null);
    if (!pin) {
      doc.update((secs) => updateAt(secs, selected, (x) => ({ ...x, presets: [] })));
      return toast(t('toast.ai', { section: name }));
    }
    const next = togglePin(cur, pin);
    if (!next) return toast(t('toast.max', { count: MAX_PINS }));
    doc.update((secs) => updateAt(secs, selected, () => next));
    toast(t(next.presets.length > cur.presets.length ? 'toast.picked' : 'toast.unpicked', { design: designName(pin), section: name }));
  };

  return (
    <div className={s.listGrid}>
      <Outline doc={doc} selected={selected} select={choose} />
      {cur ? (
        <Gallery sectionType={cur.section_type} pinned={cur.presets} filters={filters} onFilters={setFilters} onPick={pick} onHover={setHov} />
      ) : (
        <div className={s.placeholder}>{t('preview.empty')}</div>
      )}
      <PreviewPanel hint={hov && cur ? t('preview.try_on_hint', { design: designName(hov), section: section(cur.section_type) }) : undefined} onExpand={openConstructor}>
        <PageStack secs={doc.secs} selected={selected} outline tryOn={hov} onSelect={choose} />
      </PreviewPanel>
    </div>
  );
}
